import { Capacitor } from '@capacitor/core'
import type { CheckinSettings } from '@refuelled/shared'

/**
 * The check-in reminder (M-112, ADR-024).
 *
 * A local notification scheduled on the device, never a server push. There is
 * no scheduler in this repository, and the one recurring job that exists is a
 * `setInterval` whose clock restarts on every deploy. Scheduling on the phone
 * needs no new server infrastructure, works with the phone offline, and keeps
 * the fact that somebody has this switched on off the server's schedule
 * altogether, which for this feature is worth having.
 *
 * The cost is stated rather than hidden: **there is no reminder on the web.**
 * Everything here is a no-op in a browser, and the settings page says so
 * rather than leaving somebody waiting for a notification that cannot come.
 */

/** One fixed id, so rescheduling replaces rather than stacking up. */
const NOTIFICATION_ID = 2401

/**
 * The reminder's own Android channel. On the plugin's generic default
 * channel Android gave it ordinary importance with vibration off: no banner
 * and no buzz, so on a phone set to vibrate it arrived unnoticed in the
 * shade. A channel of its own pops up and vibrates, and appears by name in
 * the phone's notification settings for anyone who wants it quieter.
 * Android fixes a channel's importance once it exists, so a change to these
 * settings needs a new id.
 */
export const CHECKIN_CHANNEL = {
  id: 'checkin',
  name: 'Daily check-in reminder',
  description: 'The "How are you today?" reminder at the time you chose.',
  /** HIGH: makes a sound and pops up on screen. */
  importance: 4,
  vibration: true,
} as const

interface LocalNotificationsPlugin {
  checkPermissions(): Promise<{ display: string }>
  requestPermissions(): Promise<{ display: string }>
  schedule(options: { notifications: unknown[] }): Promise<unknown>
  createChannel(channel: typeof CHECKIN_CHANNEL): Promise<void>
  cancel(options: { notifications: Array<{ id: number }> }): Promise<void>
}

/**
 * The plugin, wrapped. Never resolve a promise with a Capacitor plugin
 * itself: resolving looks for a `.then` on it, and a plugin proxy throws for
 * any method it does not implement, so the promise rejects with
 * "LocalNotifications.then() is not implemented". That is how the reminder
 * went unscheduled on every Android phone until 0.59.3 (see status-bar.ts).
 */
async function plugin(): Promise<{ notifications: LocalNotificationsPlugin } | null> {
  if (!Capacitor.isNativePlatform()) return null
  try {
    const mod = await import('@capacitor/local-notifications')
    return { notifications: mod.LocalNotifications as unknown as LocalNotificationsPlugin }
  } catch {
    // The plugin is not in this build. A missing reminder must never stop
    // somebody using the page itself.
    return null
  }
}

/**
 * Put the reminder where the settings say it should be.
 *
 * Called whenever the settings change and whenever a check-in is answered.
 * Cancelling first means there is only ever one, so a person who changes the
 * time three times gets one notification rather than three.
 */
export async function scheduleCheckinReminder(
  settings: CheckinSettings,
  options: {
    answeredToday?: boolean
    /**
     * Whether to ask for notification permission if it is missing. True when
     * the person has just changed the setting; false when the app is only
     * putting the reminder back, where a prompt would arrive unbidden.
     */
    ask?: boolean
  } = {},
): Promise<'scheduled' | 'cancelled' | 'unavailable' | 'denied'> {
  const loaded = await plugin()
  if (!loaded) return 'unavailable'
  const { notifications } = loaded

  await notifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] }).catch(() => undefined)

  if (!settings.enabled || !settings.remindAt) return 'cancelled'

  let permission = await notifications.checkPermissions().catch(() => ({ display: 'denied' }))
  if (permission.display !== 'granted' && options.ask !== false) {
    permission = await notifications.requestPermissions().catch(() => ({ display: 'denied' }))
  }
  if (permission.display !== 'granted') return 'denied'

  const [hour, minute] = settings.remindAt.split(':').map(Number)

  // Creating a channel that exists already changes nothing, so this is safe every time.
  await notifications.createChannel(CHECKIN_CHANNEL).catch(() => undefined)

  await notifications.schedule({
    notifications: [
      {
        id: NOTIFICATION_ID,
        title: 'How are you today?',
        // Deliberately flat. A reminder that cheerled would be answering the
        // question for somebody before they had.
        body: 'A moment to say how you are getting on.',
        schedule: {
          // Every day at the chosen time, in the phone's own clock.
          on: { hour, minute },
          allowWhileIdle: true,
        },
        channelId: CHECKIN_CHANNEL.id,
        extra: { route: '/check-in' },
      },
    ],
  })

  // Today is already answered, so the next one it fires is tomorrow's, which
  // is what a daily repeat does anyway. Nothing more to do.
  void options.answeredToday
  return 'scheduled'
}

/** Whether a reminder is possible at all here, for the settings copy to say so. */
export function remindersAvailable(): boolean {
  return Capacitor.isNativePlatform()
}
